import {
  type IssueMetadataEuActual,
  type RegulatoryScheme,
  SustainabilityTier,
} from '../../unit-meta/index.js';
import {
  getActualGHGIntensityAirportDelivery,
  getValue,
  tier1Schemes,
  tierASchemes,
  tierCFeedStock,
} from '../constants.js';

export namespace CalculationsV1EuActual {
  export const equations = {
    [SustainabilityTier.A]: ({
      greenhouseGas,
    }: IssueMetadataEuActual) => {
      return (
        greenhouseGas.inducedLandUseChange
        + greenhouseGas.extractionEmissions
        + greenhouseGas.annualisedEmissions
        + greenhouseGas.processingEmissions
        + greenhouseGas.transportEmissions
        + greenhouseGas.inUseFuelEmissions
        + getActualGHGIntensityAirportDelivery(greenhouseGas)
        + getValue(greenhouseGas.displacementEmissions)
        - (greenhouseGas.savingsFromCarbonAndStorage ?? 0)
        - greenhouseGas.savingsFromCarbonAndReplacement
        - greenhouseGas.savingsFromExcess
      );
    },

    [SustainabilityTier.B]: ({
      greenhouseGas,
    }: IssueMetadataEuActual) => {
      return (
        greenhouseGas.inducedLandUseChange
        + greenhouseGas.extractionEmissions
        + greenhouseGas.annualisedEmissions
        + greenhouseGas.processingEmissions
        + greenhouseGas.transportEmissions
        + greenhouseGas.inUseFuelEmissions
        + getActualGHGIntensityAirportDelivery(greenhouseGas)
        - (greenhouseGas.savingsFromCarbonAndStorage ?? 0)
        - greenhouseGas.savingsFromCarbonAndReplacement
        - greenhouseGas.savingsFromExcess
      );
    },

    [SustainabilityTier.C]: ({
      greenhouseGas,
    }: IssueMetadataEuActual) => {
      return (
        greenhouseGas.extractionEmissions
        + greenhouseGas.annualisedEmissions
        + greenhouseGas.processingEmissions
        + greenhouseGas.transportEmissions
        + greenhouseGas.inUseFuelEmissions
        + getActualGHGIntensityAirportDelivery(greenhouseGas)
        - greenhouseGas.savingsFromSoil
        - (greenhouseGas.savingsFromCarbonAndStorage ?? 0)
        - greenhouseGas.savingsFromCarbonAndReplacement
        - greenhouseGas.savingsFromExcess
      );
    },
  };

  const verifyTierB = (metadata: IssueMetadataEuActual) => {
    const { product: { jetFuelBaselineCarbonIntensity, feedStock }, certification: { isLowILUCRiskConfirmed }, greenhouseGas: { savingsFromCarbonAndStorage } } = metadata;

    const baseline = Number(jetFuelBaselineCarbonIntensity?.replace('g', ''));

    if (!(1 - (equations[SustainabilityTier.B](metadata) / baseline) >= 0.6)) {
      return SustainabilityTier.C;
    }

    if (!feedStock?.wasCertifiedAsProductionResidue) {
      if (isLowILUCRiskConfirmed) {
        return SustainabilityTier.B;
      }
      return SustainabilityTier.C;
    }

    if (!savingsFromCarbonAndStorage) {
      return SustainabilityTier.B;
    }

    if (feedStock?.wasCertifiedAsProductionResidue) {
      return SustainabilityTier.B;
    }

    if (isLowILUCRiskConfirmed) {
      return SustainabilityTier.B;
    }

    if (!(1 - (equations[SustainabilityTier.B](metadata) + savingsFromCarbonAndStorage / baseline) >= 0.6)) {
      return SustainabilityTier.B;
    }

    return SustainabilityTier.C;
  };

  export const calculateSustainabilityTierForEUActual = (
    metadata: IssueMetadataEuActual,
  ) => {
    const { product: { feedStock, conversionProcess, jetFuelBaselineCarbonIntensity }, certification: { scheme, isLowILUCRiskConfirmed, isLowDisplacementRiskConfirmed }, batch: { regulatorySchemesDeclaration }, greenhouseGas: { displacementEmissions } } = metadata;
    const baseline = Number(jetFuelBaselineCarbonIntensity?.replace('g', ''));

    if (feedStock?.name && tierCFeedStock.includes(feedStock.name)) {
      return SustainabilityTier.C;
    }

    if (conversionProcess === 'ptl' && !tierASchemes.includes(scheme)) {
      return SustainabilityTier.C;
    }

    if (!isLowILUCRiskConfirmed) {
      return verifyTierB(metadata);
    }

    if (!feedStock?.wasCertifiedAsProductionResidue && regulatorySchemesDeclaration.some((x: RegulatoryScheme) =>
      tier1Schemes.includes(x),
    )) {
      return SustainabilityTier.C;
    }

    if (!displacementEmissions) {
      return verifyTierB(metadata);
    }

    if (!isLowDisplacementRiskConfirmed) {
      return verifyTierB(metadata);
    }

    if (regulatorySchemesDeclaration.some((x: RegulatoryScheme) =>
      tier1Schemes.includes(x),
    )) {
      return SustainabilityTier.C;
    }

    if (!(1 - (equations[SustainabilityTier.A](metadata) / baseline) >= 0.6)) {
      return verifyTierB(metadata);
    }

    return SustainabilityTier.A;
  };

}
